import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import {
  autoSignIn,
  confirmResetPassword,
  confirmSignUp,
  ConfirmSignUpOutput,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword as authResetPassword,
  ResetPasswordOutput,
  signIn as authSignIn,
  SignInOutput,
  signOut as authSignOut,
  signUp as authSignUp,
  SignUpOutput
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Reducer, useImmerReducer } from 'use-immer';
import { useLazyQuery } from '@apollo/client';
import defer from 'lodash-es/defer';
import { UserStatus } from '@simplyanvil/cl-types';
import { sanitizePhone } from '@/libs/utils';
import { UserOrganisation } from './OrganisationContext';
import { GET_USER_DETAILS } from '../graphql/queries';
import { useBugsnag } from '@/contexts/BugsnagContext';
import { jwtDecode } from 'jwt-decode';
import { SignOutInput } from 'aws-amplify/auth';

export enum AuthStatus {
  LOADING,
  ANONYMOUS,
  AUTHENTICATED,
  PENDING_SIGN_IN,
  SIGN_IN_ERROR,
  SIGN_IN_TOTP,
  PENDING_SIGN_IN_TOTP,
  SIGN_IN_TOTP_ERROR,
  PENDING_SIGN_UP,
  SIGN_UP_ERROR,
  SIGN_UP_CONFIRM,
  PENDING_SIGN_UP_CONFIRM,
  SIGN_UP_CONFIRM_ERROR,
  PENDING_SIGN_OUT,
  RESET_PASSWORD,
  RESET_PASSWORD_ERROR,
  PENDING_RESET_PASSWORD,
  RESET_PASSWORD_CONFIRM,
  RESET_PASSWORD_CONFIRM_ERROR,
  PENDING_RESET_PASSWORD_CONFIRM
}

export type AuthUserType = {
  id: string;
  email: string;
  phone?: string;
  fName?: string;
  lName?: string;
  access?: {
    groups: string[];
    scopes: string[];
  };
  organisations: UserOrganisation[];
};

export type AuthStateContextType = {
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: Error;
  user: AuthUserType | undefined | null;
  idToken: string | undefined;
  status: UserStatus | undefined;
  sessionStatus: AuthStatus;
  tmpUser?: string;
  organisations: UserOrganisation[];
  isSSO?: boolean;
};

type AuthSignUpParams = {
  password: string;
  email: string;
  fName: string;
  lName: string;
  phone: string;
};

export type AuthDispatchContextType = {
  dispatch: (action: any) => void;
  signIn: ({ email, password }: { email: string; password: string }) => Promise<Partial<SignInOutput>>;
  signUp: (data: AuthSignUpParams) => Promise<Partial<SignUpOutput>>;
  signUpConfirm: ({ email, code }: { email?: string; code: string }) => Promise<Partial<ConfirmSignUpOutput>>;
  resetPassword: ({
    email
  }: {
    email: string;
  }) => Promise<Partial<ResetPasswordOutput> & { unverifiedAccount?: boolean }>;
  resetPasswordConfirm: ({
    code,
    newPassword,
    email
  }: {
    code: string;
    newPassword: string;
    email?: string | undefined;
  }) => Promise<void>;
  signOut: () => Promise<void>;
};

type AuthContextProps = {
  children: React.ReactNode;
};

export const AuthStateContext = createContext({} as AuthStateContextType);
export const AuthDispatchContext = createContext({} as AuthDispatchContextType);

const authReducer: Reducer<
  AuthStateContextType & {
    tmpUser?: { email?: string; password?: string };
  }
> = (draft, action) => {
  switch (action.type) {
    case 'signOut':
    case 'anonymousSession':
    case 'clearSession': {
      draft.user = undefined;
      draft.idToken = undefined;
      draft.isAuthenticated = false;
      draft.isLoading = false;
      draft.error = undefined;
      draft.sessionStatus = AuthStatus.ANONYMOUS;
      draft.tmpUser = undefined;
      draft.organisations = undefined;
      break;
    }
    case 'createdNewOrg': {
      draft.organisations = [action.organisation, ...draft.organisations];
      break;
    }
    case 'clearErrors': {
      draft.error = undefined;
      break;
    }
    case 'signingIn': {
      draft.sessionStatus = AuthStatus.PENDING_SIGN_IN;
      draft.error = undefined;
      break;
    }
    case 'signInError': {
      draft.sessionStatus = AuthStatus.SIGN_IN_ERROR;
      draft.error = action.error;
      break;
    }
    case 'signInTotp': {
      draft.sessionStatus = AuthStatus.PENDING_SIGN_IN_TOTP;
      draft.error = undefined;
      break;
    }
    case 'signInWithTotp': {
      draft.sessionStatus = AuthStatus.PENDING_SIGN_IN_TOTP;
      draft.error = undefined;
      break;
    }
    case 'signInTotpError': {
      draft.sessionStatus = AuthStatus.SIGN_IN_TOTP_ERROR;
      draft.error = action.error;
      break;
    }
    case 'signUpError': {
      draft.sessionStatus = AuthStatus.SIGN_UP_ERROR;
      draft.error = action.error;
      break;
    }
    case 'signedIn': {
      draft.user = action.user as AuthUserType;
      draft.idToken = action.user?.signInUserSession?.idToken?.jwtToken;
      draft.isAuthenticated = true;
      draft.error = undefined;
      draft.isLoading = false;
      draft.sessionStatus = AuthStatus.AUTHENTICATED;
      // draft.status = action.user.status;

      // Other details
      draft.organisations = action.user.organisations || [];
      draft.tmpUser = undefined;

      // SSO
      draft.isSSO = action.isSSO || false;

      break;
    }
    case 'resetPassword': {
      draft.sessionStatus = AuthStatus.RESET_PASSWORD;
      draft.error = undefined;
      break;
    }
    case 'resettingPassword': {
      draft.sessionStatus = AuthStatus.PENDING_RESET_PASSWORD;
      draft.error = undefined;
      break;
    }
    case 'resetPasswordError': {
      draft.sessionStatus = AuthStatus.RESET_PASSWORD_ERROR;
      draft.error = action.error;
      break;
    }
    case 'resetPasswordConfirm': {
      draft.sessionStatus = AuthStatus.RESET_PASSWORD_CONFIRM;
      draft.error = undefined;
      draft.tmpUser = action.user;
      break;
    }
    case 'confirmingResetPassword': {
      draft.sessionStatus = AuthStatus.PENDING_RESET_PASSWORD_CONFIRM;
      draft.error = undefined;
      break;
    }
    case 'resetPasswordConfirmError': {
      draft.sessionStatus = AuthStatus.RESET_PASSWORD_CONFIRM_ERROR;
      draft.error = action.error;
      break;
    }
    case 'signingUp': {
      draft.sessionStatus = AuthStatus.PENDING_SIGN_UP;
      draft.error = undefined;
      break;
    }
    case 'signUpConfirm': {
      draft.sessionStatus = AuthStatus.SIGN_UP_CONFIRM;
      draft.error = undefined;
      draft.tmpUser = action.user;
      break;
    }
    case 'confirmingSignUp': {
      draft.sessionStatus = AuthStatus.PENDING_SIGN_UP_CONFIRM;
      draft.error = undefined;
      break;
    }
    case 'signUpConfirmError': {
      draft.sessionStatus = AuthStatus.SIGN_UP_CONFIRM_ERROR;
      draft.error = action.error;
      break;
    }
    case 'signingOut': {
      draft.sessionStatus = AuthStatus.PENDING_SIGN_OUT;
      draft.isLoading = true;
      break;
    }
    default: {
      throw new Error(`Unhandled dispatch: ${action.type}`);
    }
  }
};

const initialState = {
  user: undefined,
  idToken: undefined,
  isAuthenticated: false,
  isLoading: true,
  error: undefined,
  sessionStatus: AuthStatus.LOADING,
  organisations: undefined,
  tmpUser: undefined // Used for sign-up confirmation
};

const isSSOUser = async (): Promise<boolean> => {
  try {
    const { tokens } = await fetchAuthSession();
    const idToken = tokens?.idToken;
    if (!idToken) return false;

    const decodedToken = jwtDecode(idToken.toString()) as { identities?: unknown[] };
    return !!decodedToken.identities;
  } catch (error) {
    console.error('Error checking federated status:', error);
    return false;
  }
};

export const AuthProvider = ({ children }: AuthContextProps): any => {
  const [state, dispatch] = useImmerReducer<any, any>(authReducer, initialState);
  const { setBugsnagUser } = useBugsnag();
  const { tmpUser } = state;
  const [getUser, { client: apolloClient }] = useLazyQuery(GET_USER_DETAILS, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  const invalidateClientSession = useCallback(async () => {
    try {
      await authSignOut();
    } catch (error) {
      console.error('AuthProvider:invalidateClientSession:error', error);
    }
    dispatch({ type: 'clearSession' });
  }, [dispatch]);

  const getServerUser = useCallback(async (): Promise<AuthUserType> => {
    const { data } = await getUser();
    if (!data?.user) {
      throw new Error('Authenticated user is not recognised by the server');
    }
    return data.user as AuthUserType;
  }, [getUser]);

  const setAuthenticatedUser = useCallback(
    async (user: AuthUserType): Promise<void> => {
      const isSSO = await isSSOUser();
      dispatch({ type: 'signedIn', user, isSSO });
      setBugsnagUser(user.id, user.email, user.fName);
    },
    [dispatch, setBugsnagUser]
  );

  const resetPassword = useCallback(
    async ({ email }: { email: string }): Promise<Partial<ResetPasswordOutput> & { unverifiedAccount?: boolean }> => {
      dispatch({ type: 'resettingPassword' });
      try {
        const { isPasswordReset, nextStep } = await authResetPassword({ username: email });

        if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
          dispatch({ type: 'resetPasswordConfirm', user: { email: email.toLowerCase() } });
        }

        return { isPasswordReset, nextStep };
      } catch (err: any) {
        // Cognito refuses to reset a password for an account that never verified its
        // email/phone (e.g. sign-up was never confirmed). Rather than dead-ending the
        // user on a generic error, resend the sign-up confirmation code and route them
        // into the existing CONFIRM_SIGN_UP flow so they can verify and then retry.
        if (
          err?.name === 'InvalidParameterException' &&
          /no registered\/verified email or phone_number/i.test(err?.message || '')
        ) {
          try {
            await resendSignUpCode({ username: email });
          } catch (resendErr) {
            console.error('AuthProvider:resetPassword:resendSignUpCode:error', resendErr);
          }
          dispatch({ type: 'signUpConfirm', user: { email: email.toLowerCase() } });
          return { isPasswordReset: false, unverifiedAccount: true };
        }

        dispatch({ type: 'resetPasswordError', error: err });
        console.error('AuthProvider:resetPassword:error', err);

        // throw err;

        return { isPasswordReset: false };
      }
    },
    [dispatch]
  );

  const resetPasswordConfirm = useCallback(
    async ({ code, newPassword, email }: { code: string; newPassword: string; email?: string }): Promise<void> => {
      dispatch({ type: 'confirmingResetPassword' });
      try {
        const username = tmpUser?.email || email;
        await confirmResetPassword({
          newPassword,
          username,
          confirmationCode: code
        });

        // F2 — pentest fix: revoke any pre-existing refresh tokens on other
        // devices. We sign in briefly to obtain a session, then call
        // globalSignOut, then force a fresh sign-in.
        //
        // Skip the globalSignOut path when an MFA challenge is pending: the
        // session is still in-flight (the user must complete TOTP confirm via
        // `confirmSignIn`), and tearing it down here breaks that flow. In that
        // branch we rely on `confirmResetPassword` itself having rotated the
        // user's tokens via Cognito's token-revocation behaviour.
        const { nextStep } = await authSignIn({ username, password: newPassword });

        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          dispatch({ type: 'signInTotp', user: { email: (username ?? '').toLowerCase(), newPassword } });
        } else {
          try {
            await authSignOut({ global: true });
          } catch (signOutErr) {
            console.error('AuthProvider:resetPassword:globalSignOut:error', signOutErr);
          }
          // Force a fresh sign-in after revocation.
          dispatch({ type: 'signOut' });
        }
      } catch (err) {
        dispatch({ type: 'resetPasswordConfirmError', error: err });
        console.error('AuthProvider:resetPassword:error', err);

        // throw err;
      }
    },
    [dispatch, tmpUser]
  );

  const signIn = useCallback(
    async ({ email, password }: { email: string; password: string }): Promise<Partial<SignInOutput>> => {
      dispatch({ type: 'signingIn' });
      try {
        const { isSignedIn, nextStep } = await authSignIn({ username: email.toLowerCase(), password });

        // Account exists but sign-up was never confirmed. Amplify maps Cognito's
        // UserNotConfirmedException to this step without sending a new code, and the
        // original one may have expired, so resend it before showing the verify form.
        if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          try {
            await resendSignUpCode({ username: email.toLowerCase() });
          } catch (resendErr) {
            console.error('AuthProvider:signIn:resendSignUpCode:error', resendErr);
          }
          dispatch({ type: 'signUpConfirm', user: { email: email.toLowerCase(), password } });
        }

        if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          dispatch({ type: 'signInTotp', user: { email: email.toLowerCase(), password } });
        }

        return { isSignedIn, nextStep };
      } catch (err) {
        dispatch({ type: 'signInError', error: err });
        console.error('AuthProvider:signIn:error', err);

        // throw err;

        return { isSignedIn: false };
      }
    },
    [dispatch]
  );

  const signUp = useCallback(
    async ({
      email,
      fName,
      lName,
      password,
      phone
    }: AuthSignUpParams): Promise<
      Partial<SignUpOutput> & {
        error?: Error;
      }
    > => {
      dispatch({ type: 'signingUp' });

      try {
        const { isSignUpComplete, userId, nextStep } = await authSignUp({
          password,
          username: email.toLowerCase(),
          options: {
            autoSignIn: true,
            userAttributes: {
              email: email.toLowerCase(),
              name: fName,
              family_name: lName,
              phone_number: sanitizePhone(phone)
            }
          }
        });

        if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
          dispatch({ type: 'signUpConfirm', user: { email: email.toLowerCase(), password } });
        }

        if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
          await autoSignIn();
        }

        return { isSignUpComplete, userId, nextStep };
      } catch (err: any) {
        dispatch({ type: 'signUpError', error: err });
        console.error('AuthProvider:signUp:error', err);

        // throw err;

        return { isSignUpComplete: false };
      }
    },
    [dispatch]
  );

  const signUpConfirm = useCallback(
    async ({ email, code }: { email?: string; code: string }): Promise<Partial<ConfirmSignUpOutput>> => {
      dispatch({ type: 'confirmingSignUp' });
      try {
        const { isSignUpComplete, userId, nextStep } = await confirmSignUp({
          username: tmpUser?.email || email,
          confirmationCode: code
        });

        if (nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
          await autoSignIn();
          return { isSignUpComplete, userId, nextStep };
        }

        return { isSignUpComplete, userId, nextStep };
      } catch (err: any) {
        dispatch({ type: 'signUpConfirmError', error: err });
        console.error('AuthProvider:signUpConfirm:error', err);

        // throw err;

        return { isSignUpComplete: false };
      }
    },
    [dispatch, tmpUser]
  );

  const signOut = useCallback(async (): Promise<void> => {
    dispatch({ type: 'signingOut' });
    const performSignOut = async () => {
      try {
        const isSSO = await isSSOUser();

        const signOutParams: SignOutInput = isSSO
          ? {
              global: true,
              oauth: {
                redirectUrl: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_SIGN_OUT
              }
            }
          : { global: true };

        await authSignOut(signOutParams);

        dispatch({ type: 'signOut' });

        // Bugsnag
        setBugsnagUser(null, null, null);

        // Clear Apollo cache
        await apolloClient.clearStore();
      } catch (err) {
        console.error('AuthProvider:signOut:error', err);
      }
    };

    defer(() => void performSignOut());
  }, [apolloClient, dispatch, setBugsnagUser]);

  // Check auth status
  const checkAuthStatus = useCallback(async () => {
    let hasClientSession = false;
    try {
      const currentUser = await getCurrentUser();
      hasClientSession = !!currentUser;

      // Fetch User details
      if (currentUser) {
        const user = await getServerUser();
        await setAuthenticatedUser(user);
      }
    } catch (e) {
      console.error('AuthProvider:checkAuthStatus:error', e);
      if (hasClientSession) {
        await invalidateClientSession();
      } else {
        dispatch({ type: 'anonymousSession' });
      }
    }
  }, [dispatch, getServerUser, invalidateClientSession, setAuthenticatedUser]);

  const hubHandler = useCallback(
    async ({ payload }) => {
      // eslint-disable-next-line no-console
      console.info({ action: 'AuthProvider:hub:event', data: payload });
      if (payload.event === 'signIn' || payload.event === 'signedIn') {
        try {
          const user = await getServerUser();
          await setAuthenticatedUser(user);
        } catch (error) {
          console.error('AuthProvider:hub:signIn:error', error);
          await invalidateClientSession();
        }
      }
      if (payload.event === 'tokenRefresh_failure' || payload.event === 'signedOut') {
        dispatch({ type: 'clearSession' });
      }
    },
    [dispatch, getServerUser, invalidateClientSession, setAuthenticatedUser]
  );

  useEffect(() => {
    // Defer past hydration commit so dispatching anonymousSession doesn't update
    // the Suspense subtree mid-hydration (React #421).
    const id = setTimeout(() => {
      checkAuthStatus().catch((err) => console.error('AuthProvider:checkAuthStatus:error', err));
    }, 0);
    return () => clearTimeout(id);
  }, [checkAuthStatus]);

  // Amplify Hub handling
  useEffect(() => {
    const hubListenerCancel = Hub.listen('auth', hubHandler);

    return () => {
      hubListenerCancel();
    };
  }, [hubHandler]);

  const dispatchContextValue = useMemo(
    () => ({
      signIn,
      signUp,
      signUpConfirm,
      signOut,
      resetPassword,
      resetPasswordConfirm,
      dispatch
    }),
    [signIn, signUp, signUpConfirm, signOut, resetPassword, resetPasswordConfirm, dispatch]
  );

  return (
    <AuthDispatchContext.Provider value={dispatchContextValue}>
      <AuthStateContext.Provider value={state}>{children}</AuthStateContext.Provider>
    </AuthDispatchContext.Provider>
  );
};

export const useAuthState = () => {
  const c = useContext(AuthStateContext);
  if (!c) throw new Error('Cannot use useAuthState when not under the AuthProvider');
  return c;
};

export const useAuthDispatch = () => {
  const c = useContext(AuthDispatchContext);
  if (!c) throw new Error('Cannot use useAuthDispatch when not under the AuthProvider');
  return c;
};
